import { useMemo, useState, type FC } from 'react';
import type { HeroDetailedProps } from './types';

import styled, { css } from 'styled-components';
import {
  COLOR_ORANGE,
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  generateAccessKey
} from 'shared/helpers/index';
import { useBook, useSales, useWebsiteEditor } from 'shared/hooks/index';

import { Button, Wrapper, Mockup, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { Image as ImageComp } from 'shared/components/Common';
import { ConditionalImage } from '../ConditionalImage';

import { shade } from 'shared/helpers/utils';
import useGeneralActions from '../hooks/useGeneralActions';

const HeroDetailed = styled.div`
  position: relative;
  overflow-x: hidden;
  min-height: 693px;
`;

const WrapperTitle = styled((props) => <Wrapper {...props} />)`
  padding: 96px 0 44px;
  display: flex;
  justify-content: center;

  @media screen and (max-width: 710px) {
    padding: 80px 0 30px;
  }

  @media screen and (max-width: 480px) {
    padding: 80px 0 10px;
  }
`;

const StyledWrapper = styled((props) => <Wrapper {...props} />)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0 0;
  max-width: 100%;
  gap: 50px;
  margin-bottom: 80px;

  @media screen and (max-width: 991px) {
    padding: 10px 0 0;
    margin-bottom: 50px;
  }

  @media screen and (max-width: 906px) {
    gap: 40px;
    flex-direction: column-reverse;
    margin-bottom: 40px;
    padding: 10px 0 0;
  }

  @media screen and (max-width: 480px) {
    padding: 10px 0 0;
  }
`;

const TitleWrapper = styled.div`
  width: 655px;
  position: relative;
  z-index: 7;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 0 15px;
    `}

    @media screen and (max-width: 480px) {
    margin-top: 0;
  }
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  line-height: 1.25em;
  color: #fff;
  position: relative;
  z-index: 5;
  text-align: center;
  padding: 0 80px;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}

  @media screen and (max-width: 710px) {
    padding: 0px;
  }
`;

const DescWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 5;
  padding: 20px 10% 140px;
  text-align: center;

  @media screen and (max-width: 991px) {
    padding-top: 0;
  }

  @media screen and (max-width: 575px) {
    padding: 10px 0 96px;
  }
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  margin-bottom: 50px;
  line-height: 1.35em;
  color: #fff;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 59px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 40px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const BtnInner = styled.div`
  display: flex;
  justify-content: center;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
  `}
`;

const buttonStyles = css`
  max-width: 465px;
  display: inline-block;
  padding-left: 50px;
  padding-right: 50px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      max-width: 345px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      max-width: 380px;
      padding: 19px 32px;
    `}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

const ButtonStyled = styled(Button)`
  ${buttonStyles}
`;

const RelativeWrapper = styled.div`
  position: relative;
  z-index: 12;
  width: 704px;
  height: 404px;
  display: flex;
  justify-content: center;
  align-items: center;
  right: -4%;

  @media screen and (max-width: 991px) {
    right: -3%;
    height: 464px;
  }

  @media screen and (max-width: 710px) {
    left: 0;
    right: 0;
    height: 404px;
  }

  @media screen and (max-width: 480px) {
    width: 580px;
    height: 364px;
  }
`;

const StyledMockup = styled((props) => <Mockup {...props} />)`
  width: 506px;
  height: auto;
  object-fit: fill;
  z-index: 2;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 438px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 340px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 370 &&
    `
    width: 270px;
  `}
`;

const BenefitWrapper = styled.div`
  position: relative;
  z-index: 20;
`;

const Benefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
  width: 100%;
  position: relative;
  z-index: 24;

  @media screen and (max-width: 480px) {
    gap: 20px;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  gap: 10px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: row;
    width: 100%;
    border-radius: 12px;

    &:not(:last-child) {
      margin-bottom: 10px;
    }
    `}
`;

const IconBenefit = styled.div`
  width: 62px;
  height: 62px;
  margin-right: 18px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0px 2px 8.1px rgba(31, 31, 31, 0.12);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 30px;
    height: 30px;
  }

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 52px;
      height: 52px;
      margin-bottom: 8px;
    `}
`;

const BenefitTxtInner = styled.div`
  width: 78%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 100%;
      text-align: left;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
      width: 80%;
    `}
`;

const TitleBenefit = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  margin-bottom: 6px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    margin-bottom: 4px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const TextBenefit = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.3em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const WhiteInner = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 3;
  overflow: hidden;
`;

const RelativeWhite = styled.div<{ isWhiteBg?: boolean; oppositeBackgroundColor?: string }>`
  position: relative;
  width: 1920px;
  height: 100%;
  margin: 0 auto;
  opacity: ${(props) => (props.isWhiteBg ? 1 : 0.8)};

  .hero-white-v5 {
    border-radius: 50%;
    filter: blur(128px);
    background: ${(props) => (props.isWhiteBg ? props.oppositeBackgroundColor : '#fff')};
    position: absolute;
    margin: 0 auto;
    left: 0;
    right: 0;
  }

  .hero-white-v5_one {
    width: 810px;
    height: 810px;
    opacity: 0.2;
    bottom: -20%;
    right: -44%;
  }

  .hero-white-v5_two {
    width: 522px;
    height: 522px;
    opacity: 0.06;
    right: -38%;
    bottom: 25%;
  }

  .hero-white-v5_three {
    width: 609px;
    height: 609px;
    opacity: 0.15;
    right: -28%;
    bottom: 30%;
  }

  .hero-white-v5_four {
    width: 1307px;
    height: 1307px;
    opacity: 0.2;
    left: -52%;
    bottom: -74%;
  }

  .hero-white-v5_five {
    width: 322px;
    height: 322px;
    opacity: 0.23;
    left: -78%;
    top: 1%;
  }

  .hero-white-v5_six {
    width: 322px;
    height: 322px;
    opacity: 0.24;
    right: -78%;
    top: 1%;
  }

  @media screen and (max-width: 575px) {
    .hero-white-v5_three {
      width: 609px;
      height: 609px;
      opacity: 0.35;
      right: 22%;
      bottom: auto;
      top: -3%;
    }

    .hero-white-v5_six {
      width: 322px;
      height: 322px;
      opacity: 0.54;
      left: 0;
      right: auto;
      top: 21%;
    }

    .hero-white-v5_two {
      width: 382px;
      height: 382px;
      opacity: 0.5;
      right: auto;
      bottom: 15%;
    }
  }
`;

const ColoredBg = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    height: 100%;
    `}
`;

const ImageRelative = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const AbsoluteInner = styled.div`
  position: absolute;
  top: 6.5%;
  left: 18.1%;
  z-index: 1;

  @media screen and (max-width: 710px) {
    top: 11%;
  }

  @media screen and (max-width: 480px) {
    top: 15%;
  }
`;

const LaptopImg = styled.div`
  width: 915px;
  height: 470px;
  position: absolute;
  top: -4%;
  left: -27.2%;
  right: 0;
  margin: 0 auto;
  z-index: 1;

  @media screen and (max-width: 906px) {
    left: -17%;
    right: 0;
    margin: 0 auto;
  }

  @media screen and (max-width: 710px) {
    top: 0px;
    left: 0px;
    right: 0;
    width: 100%;
    height: 100%;
  }
`;

const Laptop = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
  height: 380px;
  width: 583px;

  max-height: 380px;
  max-width: 583px;

  @media screen and (max-width: 710px) {
    width: 452px !important;
    height: 296px !important;
  }

  @media screen and (max-width: 480px) {
    width: 372px !important;
    height: 240px !important;
  }
`;

const BookMediaImg = styled(ImageComp)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

export const V8: FC<HeroDetailedProps> = (props) => {
  const {
    mockup,
    cover_thumbnail,
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    backgroundColor = COLOR_ORANGE,
    customBackgroundColor,
    oppositeBackgroundColor,
    textColor,
    benefits = [],
    meta,
    hidden,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { isCoverDisabled, pages, bookMedia } = useBook();
  const { pagesThumbnails, updateImageMeta, purchaseElementId, type, isDashboard } = useSales();
  const { handleUploadAction } = useGeneralActions(props);
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const [isLoading, setIsLoading] = useState(false);

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  const coverThumbnail = useMemo(() => {
    if (isCoverDisabled && (pages.length > 0 || pagesThumbnails.length > 0)) {
      return pagesThumbnails[0] || pages[0]?.image_preview;
    }
    return cover_thumbnail;
  }, [isCoverDisabled, pages, pagesThumbnails, cover_thumbnail]);

  const bookMockup = useMemo(() => {
    if (meta['image'] && !meta['image_placeholder']) {
      return meta['image'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor ? customBackgroundColor : shade(backgroundColor, 0.85);
  }, [backgroundColor, customBackgroundColor]);

  const iconColor = useMemo(() => {
    if (meta?.['background_color']?.includes('background_color4')) {
      return filterBackgroundColor('#fff', textColor);
    } else if (meta?.['background_color']?.includes('faded_color')) {
      return oppositeBackgroundColor;
    } else {
      return customBackgroundColor;
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customBackgroundColor, meta?.['background_color'], textColor, backgroundColor]);

  // Accessibility improvements
  const titleAccessKey = useMemo(() => generateAccessKey('ht', order), [order]);
  const contentAccessKey = useMemo(() => generateAccessKey('hc', order), [order]);
  const buttonAccessKey = useMemo(() => generateAccessKey('hb', order), [order]);
  const laptopAccessKey = useMemo(() => generateAccessKey('hlap', order), [order]);
  const skipLinkId = useMemo(() => `skip-to-hero-${meta['accessorKey'] || 'hero'}`, [meta]);

  const buttonType = useMemo(() => {
    return meta['url']
      ? 'external link'
      : type === 'delivery'
        ? 'delivery action'
        : 'purchase button';
  }, [meta, type]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Hero edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Button action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUploadError = (error: Error | unknown) => {
    // Announce upload error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Image upload error: ${error instanceof Error ? error.message : 'Upload failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUpload = async (file: File) => {
    try {
      setIsLoading(true);
      await handleUploadAction(file, setResize, 'book_mockup');
      setIsLoading(false);
    } catch (error) {
      handleUploadError(error);
      setIsLoading(false);
    }
  };

  return (
    <HeroDetailed
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}
      aria-label="Hero section with laptop mockup display and side benefits"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to hero detailed content</a>
      </span>

      <WrapperTitle id={skipLinkId}>
        <Title
          id={title.id}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichText(title.val, isDashboard)
          }}
          style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
          isEmpty={isTitleEmpty}
          {...(isDashboard
            ? {
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                  try {
                    updateActiveElement(e);
                  } catch (error) {
                    handleEditError(error);
                  }
                },
                onBlur: resetActiveElement,
                'data-accessor-parent': meta['accessorKey'],
                'data-accessorkey': 'title',
                'data-placeholder': '+',
                editable: true
              }
            : {
                'aria-label':
                  isTitleEmpty || !title.val?.trim() ? 'Hero section main title' : undefined,
                accessKey: titleAccessKey,
                title: `Press Alt+${titleAccessKey} to focus this hero title`,
                tabIndex: 0
              })}
        />
      </WrapperTitle>

      <StyledWrapper>
        <TitleWrapper>
          <Benefits role="region" aria-label="Key benefits and features list">
            {benefits.map(({ content, title }, index) => {
              const isTitleBenEmpty = checkTitleBenefits(index);
              const isContentBenEmpty = checkContentBenefits(index);
              const benefitAccessKey = generateAccessKey('hben', index);

              return (
                <BenefitItem
                  key={index}
                  role="article"
                  aria-label={`Benefit ${index + 1}: ${title?.val || 'Key feature'}`}
                  {...(!isDashboard && {
                    accessKey: benefitAccessKey,
                    title: `Press Alt+${benefitAccessKey} to focus benefit ${index + 1}`,
                    tabIndex: 0
                  })}>
                  <IconBenefit aria-hidden="true" role="presentation">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 15"
                      fill="none"
                      aria-hidden="true"
                      focusable="false">
                      <g clipPath="url(#clip0_5383_58089)">
                        <path
                          d="M7.75007 10.0699C7.84277 9.93837 7.88249 9.85942 7.94871 9.79363C10.7031 7.05675 13.4442 4.33303 16.1986 1.60931C16.9136 0.91193 17.8671 0.754033 18.6616 1.21457C19.7342 1.81984 19.9858 3.24091 19.2045 4.18829C19.1383 4.26724 19.0721 4.33303 18.9926 4.41198C15.7351 7.6357 12.4908 10.8594 9.23319 14.0963C8.62405 14.7016 7.90898 14.9121 7.08797 14.6358C6.80988 14.5437 6.5318 14.3595 6.31992 14.1489C4.66466 12.5305 3.03587 10.9121 1.39385 9.2673C0.811196 8.67519 0.625806 7.96465 0.917133 7.18833C1.20846 6.412 1.80436 5.97779 2.62537 5.89884C3.27423 5.84621 3.81716 6.09621 4.26739 6.55674C5.35325 7.64886 6.45234 8.72782 7.5382 9.80679C7.60441 9.87258 7.64414 9.93837 7.73683 10.0699H7.75007Z"
                          fill={iconColor || backgroundColor}
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_5383_58089">
                          <rect
                            width="18.87"
                            height="13.7897"
                            fill="white"
                            transform="translate(0.789062 0.953125)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </IconBenefit>
                  <BenefitTxtInner>
                    <TitleBenefit
                      id={title?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(title?.val, editable) }}
                      style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
                      isEmpty={isTitleBenEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'title',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            'aria-label': !title?.val?.trim()
                              ? `Benefit ${index + 1} title`
                              : undefined
                          })}
                    />
                    <TextBenefit
                      id={content?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(content?.val, editable) }}
                      style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
                      isEmpty={isContentBenEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'content',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: editable
                          }
                        : {
                            'aria-label': !content?.val?.trim()
                              ? `Benefit ${index + 1} description`
                              : undefined,
                            'aria-describedby': title?.id
                          })}
                    />
                  </BenefitTxtInner>
                </BenefitItem>
              );
            })}
          </Benefits>
        </TitleWrapper>

        <RelativeWrapper
          role="img"
          aria-label="Laptop mockup displaying book preview"
          {...(!isDashboard && {
            accessKey: laptopAccessKey,
            title: `Press Alt+${laptopAccessKey} to focus the laptop preview`,
            tabIndex: 0
          })}>
          <LaptopImg>
            <AbsoluteInner>
              {bookMockup ? (
                <ConditionalImage
                  isSalePage
                  css={imageStyles as never}
                  disabled={!editable}
                  resizeSelector={editable}
                  enableLinks
                  updateMeta={(value) => updateImageMeta(meta['accessorKey'], value, 'image_link')}
                  handleResize={({ width, height }) => {
                    setResize({ width, height });
                    updateImageMeta(meta['accessorKey'], width, 'width');
                    updateImageMeta(meta['accessorKey'], height, 'height');
                  }}
                  onUpload={handleUpload}
                  loading={isLoading}
                  alreadyUploaded={Boolean(bookMockup)}
                  imageFile={bookMockup}
                  initialPosition={{
                    x: meta['x_1'] || 0,
                    y: meta['y_1'] || 0,
                    width: meta['d_width_1'],
                    height: meta['d_height_1']
                  }}
                  meta={meta}
                  positionEnabled
                  handlePosition={(width: number, height: number, x: number, y: number) => {
                    updateImageMeta(meta['accessorKey'], x, 'x_1');
                    updateImageMeta(meta['accessorKey'], y, 'y_1');
                    updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                    updateImageMeta(meta['accessorKey'], height, 'd_height_1');
                  }}
                  imageAspectRatio={{
                    ratio: '4x3',
                    pixels:
                      resize.width && resize.height ? `${resize.width}x${resize.height}` : '800x600'
                  }}
                  ariaLabel="Book cover displayed on laptop screen - drag and drop to upload new image">
                  <BookMediaImg
                    src={bookMockup}
                    alt="Book cover preview displayed on laptop screen"
                    width={resize.width || 500}
                    height={resize.height || 500}
                    priority
                  />
                </ConditionalImage>
              ) : (
                <StyledMockup
                  mockup={mockup?.file}
                  mockup_id={mockup?.id}
                  cover_thumbnail={coverThumbnail}
                  aria-label="Book mockup displayed on laptop screen"
                />
              )}
            </AbsoluteInner>
            <ImageRelative>
              <Laptop
                src="https://pagewheel-public.s3.us-east-1.amazonaws.com/imgs/imacMockup2.png"
                alt=""
                aria-hidden="true"
                role="presentation"
              />
            </ImageRelative>
          </LaptopImg>
        </RelativeWrapper>
      </StyledWrapper>

      <BenefitWrapper>
        <Wrapper>
          <DescWrapper id="DescriptionSection">
            <Description
              id={content.id}
              dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
              style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
              isEmpty={isContentEmpty}
              {...(isDashboard
                ? {
                    onClick: (e: React.MouseEvent<HTMLElement>) => {
                      try {
                        updateActiveElement(e);
                      } catch (error) {
                        handleEditError(error);
                      }
                    },
                    onBlur: resetActiveElement,
                    'data-accessor-parent': meta['accessorKey'],
                    'data-accessorkey': 'content',
                    'data-placeholder': '+',
                    editable: true
                  }
                : {
                    role: 'textbox',
                    'aria-multiline': 'true',
                    'aria-label':
                      isContentEmpty || !content.val?.trim()
                        ? 'Hero section description'
                        : undefined,
                    'aria-describedby': title.id,
                    accessKey: contentAccessKey,
                    title: `Press Alt+${contentAccessKey} to focus this hero description`,
                    tabIndex: 0
                  })}
            />

            <BtnInner role="group" aria-label={`Hero action area - ${buttonType}`}>
              {meta['url'] || type === 'delivery' ? (
                <DownloadStyled
                  textColor={textColor}
                  button={button}
                  meta={meta}
                  outerOnClick={() => {
                    if (editable) {
                      try {
                        // empty needs only for error
                      } catch (error) {
                        handleModalError(error);
                      }
                    }
                  }}
                  {...(!isDashboard && {
                    accessKey: buttonAccessKey,
                    title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                    'aria-describedby': `${title.id} ${content.id}`
                  })}
                />
              ) : (
                <ButtonStyled
                  to={purchaseElementId}
                  button={button}
                  meta={meta}
                  outerOnClick={() => {
                    if (editable) {
                      try {
                        // empty needs only for error
                      } catch (error) {
                        handleModalError(error);
                      }
                    }
                  }}
                  {...(!isDashboard && {
                    accessKey: buttonAccessKey,
                    title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                    'aria-describedby': `${title.id} ${content.id}`
                  })}
                />
              )}
            </BtnInner>
          </DescWrapper>
        </Wrapper>
      </BenefitWrapper>

      <WhiteInner aria-hidden="true" role="presentation">
        <RelativeWhite
          oppositeBackgroundColor={oppositeBackgroundColor}
          isWhiteBg={
            meta?.['background_color']?.includes('background_color4') ||
            meta?.['background_color']?.includes('faded_color')
          }>
          <div className="hero-white-v5 hero-white-v5_one"></div>
          <div className="hero-white-v5 hero-white-v5_two"></div>
          <div className="hero-white-v5 hero-white-v5_three"></div>
          <div className="hero-white-v5 hero-white-v5_four"></div>
          <div className="hero-white-v5 hero-white-v5_five"></div>
          <div className="hero-white-v5 hero-white-v5_six"></div>
        </RelativeWhite>
      </WhiteInner>

      <ColoredBg
        style={{ background: resolvedBackgroundColor }}
        aria-hidden="true"
        role="presentation"
      />
    </HeroDetailed>
  );
};

V8.displayName = 'HeroDetailedV8';
